import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { VersionInfo } from '@/types';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

export const UpdateDashboard: React.FC = () => {
  const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCurrentVersion();
  }, []);

  const fetchCurrentVersion = async (): Promise<void> => {
    try {
      const response = await axios.get<VersionInfo>('/updates/indicator-version.json');
      setCurrentVersion(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch current version information');
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Traveler Dashboard Updates</h2>
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {currentVersion && (
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Current Version</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-2xl font-semibold">{currentVersion.version}</h3>
              </div>
              <div className="space-y-2">
                <div>
                  <div className="font-semibold">Release Notes</div>
                  <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {currentVersion.releaseNotes}
                  </div>
                </div>
                {currentVersion.minAppVersion && (
                  <div>
                    <div className="font-semibold">Minimum App Version Required</div>
                    <div className="text-sm text-muted-foreground">
                      {currentVersion.minAppVersion}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-sm text-muted-foreground">
                    Download URL: {currentVersion.downloadUrl}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="font-semibold">Last Updated</div>
                <div className="text-sm text-muted-foreground">
                  {new Date(currentVersion.lastUpdated).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="font-semibold">Status</div>
                <div className="flex items-center gap-2">
                  <Badge variant="default" className="bg-green-500">
                    Active
                  </Badge>
                  <span className="text-sm text-muted-foreground">✓ Running normally</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};